import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import { ORDO_NODES, ORDO_STATEMENT, RESPONSIBILITY_IDS } from "@/content/ordo";
import { ActionLink, Container, Eyebrow, Mono, Section, SectionTitle } from "@/components/site/primitives";
import { Swap } from "@/components/site/ExplorerFrame";
import { cn } from "@/lib/utils";

const Chain = ({ active, setActive }) => (
  <ol className="grid gap-2 sm:grid-cols-5">
    {RESPONSIBILITY_IDS.map((id, i) => (
      <li key={id} className="relative">
        <button type="button" onMouseEnter={() => setActive(id)} onFocus={() => setActive(id)} onClick={() => setActive(id)}
          aria-pressed={active === id} data-testid={`home-chain-${id}`}
          className={cn("flex h-full min-h-[64px] w-full items-center gap-4 border p-3 text-left transition-colors sm:min-h-[112px] sm:flex-col sm:items-start sm:justify-between",
            active === id ? "border-o-accent bg-o-soft" : "border-o-line hover:border-o-strong")}>
          <Mono accent={active === id}>{ORDO_NODES[id].index}</Mono>
          <span className="font-display text-[15px] font-bold leading-tight">{ORDO_NODES[id].label}</span>
        </button>
        {i < 4 && <ChevronRight aria-hidden className="absolute -right-[12px] top-1/2 z-10 hidden h-4 w-4 -translate-y-1/2 text-o-dim sm:block" />}
      </li>
    ))}
  </ol>
);

const Preview = () => {
  const [active, setActive] = useState("evidence");
  const node = ORDO_NODES[active];
  return (
    <div className="border border-o-strong bg-o-surface" data-testid="home-ordo-preview">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-o-line px-5 py-3">
        <Mono accent>Structural preview</Mono>
        <Mono>Relationships only · no mechanisms</Mono>
      </div>
      <div className="p-5 sm:p-7">
        <Chain active={active} setActive={setActive} />
        <div aria-hidden className="mx-[10%] mt-2 hidden h-5 border-x border-b border-dashed border-o-strong sm:block" />
        <p className="mt-2 sm:text-center"><Mono>Continuing standing returns to evidence as reality changes</Mono></p>
        <div aria-live="polite" className="mt-6 min-h-[92px] border-l-2 border-o-accent pl-4" data-testid="home-chain-readout">
          <Swap k={active}>
            <p className="font-display text-lg font-bold">{node.label} · {node.tagline}</p>
            <p className="mt-1 text-sm text-o-dim">{node.concerns}</p>
          </Swap>
        </div>
        <div className="mt-6 grid gap-2 sm:grid-cols-5">
          <Link to="/ordo/engine" data-testid="home-ordo-engine" className="border border-o-line px-4 py-3 transition-colors hover:border-o-accent sm:col-span-4">
            <Mono>Ordo software family</Mono>
            <span className="mt-1 block font-display font-bold">Ordo Engine · present judgment</span>
          </Link>
          <Link to="/ordo/stewardship" data-testid="home-ordo-stewardship" className="border border-o-line px-4 py-3 transition-colors hover:border-o-accent">
            <Mono>Family</Mono>
            <span className="mt-1 block font-display font-bold leading-tight">Ordo Stewardship</span>
          </Link>
        </div>
      </div>
      <Link to="/ordo/infrastructure" data-testid="home-ordo-infrastructure"
        className="group flex items-center justify-between gap-4 border-t border-dashed border-o-strong px-5 py-4 transition-colors hover:bg-o-raise">
        <span>
          <Mono accent>Infrastructure opportunity</Mono>
          <span className="mt-1 block text-sm">The architecture as a whole, incorporated into intelligent systems built by other organizations. A separate licensing route.</span>
        </span>
        <ArrowUpRight className="h-5 w-5 shrink-0 text-o-dim group-hover:text-o-accent" />
      </Link>
    </div>
  );
};

export const OrdoDiscovery = () => (
  <Section theme="t-ordo" testid="home-ordo" id="general-ordo" className="grain overflow-hidden">
    <div aria-hidden className="blueprint blueprint-fade pointer-events-none absolute inset-0 opacity-70" />
    <Container className="relative grid gap-14 py-24 lg:grid-cols-12 lg:py-32">
      <div className="lg:col-span-5">
        <Eyebrow index="02">General Ordo</Eyebrow>
        <SectionTitle className="mt-6">A governing architecture for consequential intelligence.</SectionTitle>
        <p className="mt-6 leading-relaxed text-o-dim">{ORDO_STATEMENT}</p>
        <p className="mt-4 leading-relaxed text-o-dim">
          It is applied in the Ordo software family, and it may also be incorporated as infrastructure into intelligent systems developed by other organizations.
        </p>
        <div className="mt-10">
          <ActionLink to="/ordo" testid="home-enter-ordo">Enter General Ordo</ActionLink>
        </div>
      </div>
      <div className="lg:col-span-7">
        <Preview />
      </div>
    </Container>
  </Section>
);
